import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  CDN_URL,
  DateFormatType,
  DateValue,
  NullableDateValue,
} from '@maximizer/core/shared/domain';
import { isObject } from '@maximizer/shared/util';
import { InterpolationParameters, TranslateParser } from '@ngx-translate/core';
import { isString } from '@progress/kendo-angular-common';
import {
  CldrIntlService,
  IntlService,
  Locale,
  NumberFormatOptions,
  setData,
} from '@progress/kendo-angular-intl';
import { format, formatISO, isValid, parse, parseISO } from 'date-fns';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { CountryListItem } from '../models/country.model';
import { GlobalStore } from '../state';
import { ContextService } from './context.service';

declare const LOAD_UID: string;

@Injectable()
export class LocaleService {
  readonly defaultLocale = 'en-US';
  readonly url = '{0}/assets/locales/{1}/all.json';
  readonly intl: CldrIntlService;
  private locales: { [key: string]: Locale } = {};
  private lastSetLocaleId = this.defaultLocale;

  constructor(
    intl: IntlService,
    private readonly http: HttpClient,
    private readonly context: ContextService,
    private readonly global: GlobalStore,
    private readonly parser: TranslateParser,
    @Inject(CDN_URL) public cdn: string,
  ) {
    this.intl = intl as CldrIntlService;
  }

  public set(localeId: string): Observable<Locale> {
    if (this.locales[localeId]) {
      this.intl.localeId = localeId;
      this.lastSetLocaleId = localeId;
      return of(this.locales[localeId]);
    }

    const url = this.intl.format(this.url, this.cdn, localeId);
    return this.http.get(url).pipe(
      tap((result) => {
        setData(result);

        this.locales[localeId] = result;
        this.intl.localeId = localeId;
        this.lastSetLocaleId = localeId;
      }),
      catchError(() => {
        if (localeId.indexOf('-') > -1) {
          return this.set(localeId.split('-')[0]);
        }
        return this.set(this.defaultLocale);
      }),
    );
  }

  // Never read this back from IntlService: Kendo chart axis components reset
  // its localeId to the static LOCALE_ID (en-US), which has no loaded data.
  get localeId(): string {
    return this.lastSetLocaleId;
  }

  get countryCode() {
    return this.data?.territory ?? '';
  }

  get data(): Locale {
    return this.locales[this.localeId];
  }

  getCountries(): Observable<CountryListItem[]> {
    const version = this.context.version ?? LOAD_UID;
    let language = this.localeId?.substring(0, 2) ?? 'en';

    const supportedLanguages = ['en', 'fr'];
    if (!supportedLanguages.includes(language)) {
      language = 'en';
    }

    return this.http
      .get<
        { code: string; name: string; flag_4x3: string }[]
      >(`${this.cdn}/assets/resources/country-${language}.json?v=${version}`)
      .pipe(
        map((countries) => {
          return countries.map(({ name, code, flag_4x3 }) => ({
            name,
            id: code,
            flag: flag_4x3,
          }));
        }),
      );
  }

  formatDate(date: NullableDateValue, format: DateFormatType): string {
    if (!date) {
      return '';
    }

    switch (format) {
      case 'isoDate':
        return this.formatISODate(date);
      case 'isoDateTime':
        return this.formatISODateTime(date);
      case 'userDate':
        return this.formatDateUserLocale(date);
      case 'userTime':
        return this.formatTimeUserLocale(date);
      case 'userDateTime':
        return `${this.formatDateUserLocale(date)} ${this.formatTimeUserLocale(date)}`;
      default:
        if (isString(date)) {
          date = parseISO(date);
        }

        if (typeof date === 'number') {
          date = new Date(date);
        }

        if (date && isValid(date)) {
          return this.intl.formatDate(date, format);
        }
        return '';
    }
  }

  formatISODateTime(date: DateValue): string {
    return formatISO(date, { representation: 'complete' });
  }

  formatISODate(date: DateValue): string {
    return formatISO(date, { representation: 'date' });
  }

  formatTimeUserLocale(date: NullableDateValue): string {
    return this.formatDate(date, this.context.timeFormat ?? 't');
  }

  formatDateUserLocale(date: NullableDateValue): string {
    return this.formatDate(date, this.context.dateFormat ?? 'd');
  }

  formatCurrency(value?: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }

    const multiCurrency =
      this.global.session()?.systemConfiguration?.multiCurrency;
    if (multiCurrency) {
      const currency =
        this.global.session()?.systemConfiguration.corporateCurrency;
      return `${currency} ${this.intl.formatNumber(value, 'n')}`;
    }
    return this.intl.formatNumber(value, 'c');
  }

  formatNumber(
    value?: number | null,
    format?: string | NumberFormatOptions,
  ): string {
    if (value === null || value === undefined) {
      return '';
    }

    return this.intl.formatNumber(value, format ?? 'n');
  }

  formatLargeNumber(value: number): string {
    const billion = 1_000_000_000;
    const million = 1_000_000;
    const thousand = 1_000;
    const currencyFormat: NumberFormatOptions = {
      style: 'currency',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    };

    if (value >= billion) {
      return `${this.intl.formatNumber(value / billion, currencyFormat)}B`;
    }
    if (value >= million) {
      return `${this.intl.formatNumber(value / million, currencyFormat)}M`;
    }
    if (value >= thousand) {
      return `${this.intl.formatNumber(value / thousand, currencyFormat)}K`;
    }
    return this.intl.formatNumber(value, currencyFormat);
  }

  format(format: string, ...args: unknown[]): string {
    if (
      !this.useFormat(format) &&
      args &&
      args.length > 0 &&
      isObject(args[0])
    ) {
      return (
        this.parser.interpolate(format, args[0] as InterpolationParameters) ??
        ''
      );
    }

    return this.intl.format(format, ...args);
  }

  parseDate(input: string, format: string): Date {
    const date = parse(input, format, new Date());
    return date;
  }

  formatISODateTimeUTC(date: DateValue): string {
    if (!date) {
      return '';
    }

    if (isString(date)) {
      date = parseISO(date);
    }

    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
  }

  private useFormat(rawString: string): boolean {
    return rawString.includes('{0}');
  }
}
